import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, Grid, Chip, LinearProgress, Avatar,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon,
    TrendingUp as TrendingIcon,
    AccessTime as HoursIcon,
    AttachMoney as BillableIcon,
} from '@mui/icons-material';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MONTHS = ['All', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const _cy    = new Date().getFullYear();
const YEARS  = [String(_cy - 2), String(_cy - 1), String(_cy)];

function fmtShort(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// ─── Metric card ─────────────────────────────────────────────────────────────
function MetricCard({ label, value, sub, icon: Icon, color, bg }) {
    return (
        <Box sx={{ p: 2.5, bgcolor: bg, borderRadius: 2.5, border: `1px solid ${color}22`, flex: '1 1 200px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>
                        {label}
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
                    {sub && <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.25 }}>{sub}</Typography>}
                </Box>
                <Box sx={{ bgcolor: `${color}20`, borderRadius: 1.5, p: 1, flexShrink: 0 }}>
                    <Icon sx={{ fontSize: 22, color }} />
                </Box>
            </Box>
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TimesheetAnalytics({ timesheets, teamMembers: teamMembersProp = [] }) {
    const [year,  setYear]  = useState(String(new Date().getFullYear()));
    const [month, setMonth] = useState('All');

    // Derive team members from timesheets if prop is empty (fallback for pages that don't pass it)
    const teamMembers = useMemo(() => {
        if (teamMembersProp.length > 0) return teamMembersProp;
        const seen = {};
        timesheets.forEach(ts => {
            const key = String(ts.employeeId);
            if (ts.employeeId && !seen[key]) {
                seen[key] = { id: key, name: ts.employeeName, avatar: ts.employeeAvatar || '', role: ts.employeeRole || '', dept: ts.employeeDept || '' };
            }
        });
        return Object.values(seen);
    }, [timesheets, teamMembersProp]);

    // ── Filtered timesheets ──
    const filtered = useMemo(() => {
        return timesheets.filter(ts => {
            if (ts.status === 'Draft') return false; // exclude drafts from analytics
            const monthIndex = MONTHS.indexOf(month);
            const tsMonth = ts.weekStart ? new Date(ts.weekStart).getMonth() + 1 : -1;
            if (year  && ts.weekStart && !ts.weekStart.startsWith(year)) return false;
            if (month !== 'All' && tsMonth !== monthIndex) return false;
            return true;
        });
    }, [timesheets, year, month]);

    // ── Per-employee aggregation ──
    const employeeStats = useMemo(() => {
        return teamMembers.map(member => {
            const memberTs = filtered.filter(ts => String(ts.employeeId) === String(member.id));
            const totalHrs   = memberTs.reduce((s, ts) => s + (ts.totalHours    || 0), 0);
            const billHrs    = memberTs.reduce((s, ts) => s + (ts.billableHours || 0), 0);
            const otHrs      = memberTs.reduce((s, ts) => s + (ts.overtimeHours || 0), 0);
            const submitted  = memberTs.length;
            const approved   = memberTs.filter(ts => ts.status === 'Approved').length;
            const pending    = memberTs.filter(ts => ['Submitted', 'Pending Level 2'].includes(ts.status)).length;
            const rejected   = memberTs.filter(ts => ts.status === 'Rejected').length;
            const billRate   = totalHrs > 0 ? Math.round((billHrs / totalHrs) * 100) : 0;
            return { ...member, totalHrs, billHrs, otHrs, billRate, submitted, approved, pending, rejected };
        });
    }, [filtered, teamMembers]);

    // ── Top contributors ──
    const highestContributor  = [...employeeStats].sort((a, b) => b.totalHrs  - a.totalHrs)[0];
    const bestBillable        = [...employeeStats].sort((a, b) => b.billRate  - a.billRate)[0];
    const totalTeamHours      = employeeStats.reduce((s, e) => s + e.totalHrs, 0);
    const avgBillRate         = employeeStats.length > 0
        ? Math.round(employeeStats.reduce((s, e) => s + e.billRate, 0) / employeeStats.length)
        : 0;

    // ── Weekly submission timeline (last 4 weeks based on mock data) ──
    const weeklyBreakdown = useMemo(() => {
        const weeks = {};
        filtered.forEach(ts => {
            const key = `${fmtShort(ts.weekStart)} – ${fmtShort(ts.weekEnd)}`;
            if (!weeks[key]) weeks[key] = { week: key, approved: 0, rejected: 0, pending: 0 };
            if (ts.status === 'Approved')                                       weeks[key].approved++;
            else if (ts.status === 'Rejected')                                  weeks[key].rejected++;
            else if (['Submitted', 'Pending Level 2'].includes(ts.status))     weeks[key].pending++;
        });
        return Object.values(weeks).slice(-5).reverse();
    }, [filtered]);

    const selectSx = { fontSize: 13, borderRadius: 1.5, height: 36, bgcolor: '#fff', '& .MuiSelect-select': { py: 0, fontSize: 13 } };
    const labelSx  = { fontSize: 12, lineHeight: 1, '&.MuiInputLabel-shrink': { fontSize: 12 } };

    return (
        <Box>
            {/* ── Filter Row ── */}
            <Paper elevation={0} sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2.5, p: 1.75, mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: 13, mr: 0.5 }}>Period</Typography>

                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={labelSx}>Year</InputLabel>
                        <Select value={year} label="Year" onChange={e => setYear(e.target.value)} sx={selectSx}>
                            {YEARS.map(y => <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={labelSx}>Month</InputLabel>
                        <Select value={month} label="Month" onChange={e => setMonth(e.target.value)} sx={selectSx}>
                            {MONTHS.map(m => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Typography sx={{ fontSize: 12, color: '#94a3b8', ml: 'auto' }}>
                        Based on {filtered.length} timesheet{filtered.length !== 1 ? 's' : ''} (drafts excluded)
                    </Typography>
                </Box>
            </Paper>

            {/* ── Top Metric Cards ── */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <MetricCard
                    label="Team Total Hours" value={`${totalTeamHours}h`}
                    sub={`Across ${filtered.length} timesheets`}
                    icon={HoursIcon} color="#6366f1" bg="#eef2ff"
                />
                <MetricCard
                    label="Avg Billable Rate" value={`${avgBillRate}%`}
                    sub="Team average"
                    icon={BillableIcon} color="#10b981" bg="#ecfdf5"
                />
                <MetricCard
                    label="Highest Contributor" value={highestContributor?.totalHrs ? `${highestContributor.totalHrs}h` : '—'}
                    sub={highestContributor?.name || ''}
                    icon={TrophyIcon} color="#f59e0b" bg="#fffbeb"
                />
                <MetricCard
                    label="Best Billable Rate" value={bestBillable?.billRate ? `${bestBillable.billRate}%` : '—'}
                    sub={bestBillable?.name || ''}
                    icon={TrendingIcon} color="#0891b2" bg="#ecfeff"
                />
            </Box>

            {/* ── Per-Employee Summary Table ── */}
            <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', overflow: 'hidden', mb: 3 }}>
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#475569' }}>Team Member Summary</Typography>
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Drafts excluded</Typography>
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 750 }}>
                        <TableHead>
                            <TableRow>
                                {['Employee', 'Total Hrs', 'Billable Hrs', 'Billable %', 'OT Hrs', 'Submitted', 'Approved', 'Pending', 'Rejected'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#1D4ED8', bgcolor: '#EFF6FF', whiteSpace: 'nowrap', py: 1.25 }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {employeeStats.map(emp => (
                                <TableRow key={emp.id} sx={{ '&:hover': { bgcolor: '#F1F5F9 !important' } }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <Avatar sx={{ width: 30, height: 30, bgcolor: '#6366f1', fontSize: 11, fontWeight: 700, borderRadius: 1.5 }}>
                                                {emp.avatar}
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{emp.name}</Typography>
                                                <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>{emp.dept}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6366f1' }}>{emp.totalHrs}h</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>{emp.billHrs}h</Typography>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 100 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{emp.billRate}%</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={emp.billRate}
                                                sx={{ height: 5, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: emp.billRate >= 80 ? '#10b981' : emp.billRate >= 50 ? '#f59e0b' : '#ef4444', borderRadius: 3 } }}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontSize: 12, fontWeight: emp.otHrs > 0 ? 700 : 400, color: emp.otHrs > 0 ? '#f59e0b' : '#94a3b8' }}>
                                            {emp.otHrs > 0 ? `+${emp.otHrs}h` : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{emp.submitted}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={emp.approved} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: '#E0F2F1', color: '#00796b' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={emp.pending} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: '#e3f2fd', color: '#1976d2' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={emp.rejected} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: emp.rejected > 0 ? '#FFEBEE' : '#f1f5f9', color: emp.rejected > 0 ? '#d32f2f' : '#94a3b8' }} />
                                    </TableCell>
                                </TableRow>
                            ))}

                            {/* ── Totals Row ── */}
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1e293b' }}>Team Total</TableCell>
                                <TableCell><Typography sx={{ fontSize: 13, fontWeight: 800, color: '#6366f1' }}>{totalTeamHours}h</Typography></TableCell>
                                <TableCell><Typography sx={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}>{employeeStats.reduce((s, e) => s + e.billHrs, 0)}h</Typography></TableCell>
                                <TableCell><Typography sx={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{avgBillRate}% avg</Typography></TableCell>
                                <TableCell><Typography sx={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>{employeeStats.reduce((s, e) => s + e.otHrs, 0)}h</Typography></TableCell>
                                <TableCell><Typography sx={{ fontSize: 12, fontWeight: 700 }}>{employeeStats.reduce((s, e) => s + e.submitted, 0)}</Typography></TableCell>
                                <TableCell><Chip label={employeeStats.reduce((s, e) => s + e.approved, 0)} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: '#E0F2F1', color: '#00796b' }} /></TableCell>
                                <TableCell><Chip label={employeeStats.reduce((s, e) => s + e.pending, 0)} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: '#e3f2fd', color: '#1976d2' }} /></TableCell>
                                <TableCell><Chip label={employeeStats.reduce((s, e) => s + e.rejected, 0)} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: employeeStats.reduce((s, e) => s + e.rejected, 0) > 0 ? '#FFEBEE' : '#f1f5f9', color: employeeStats.reduce((s, e) => s + e.rejected, 0) > 0 ? '#d32f2f' : '#94a3b8' }} /></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* ── Weekly Breakdown Table ── */}
            <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#475569' }}>Week-by-Week Breakdown</Typography>
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.25 }}>Approval status per week across the team</Typography>
                </Box>
                {weeklyBreakdown.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>No data for selected period.</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 450 }}>
                            <TableHead>
                                <TableRow>
                                    {['Week', 'Approved', 'Rejected', 'Pending Review'].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#1D4ED8', bgcolor: '#EFF6FF', py: 1.25 }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {weeklyBreakdown.map((w, i) => (
                                    <TableRow key={i} sx={{ '&:hover': { bgcolor: '#F1F5F9 !important' } }}>
                                        <TableCell sx={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{w.week}</TableCell>
                                        <TableCell>
                                            <Chip label={w.approved} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: '#E0F2F1', color: '#00796b' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={w.rejected} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: w.rejected > 0 ? '#FFEBEE' : '#f1f5f9', color: w.rejected > 0 ? '#d32f2f' : '#94a3b8' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={w.pending} size="small" sx={{ fontSize: 11, fontWeight: 700, height: 20, bgcolor: w.pending > 0 ? '#e3f2fd' : '#f1f5f9', color: w.pending > 0 ? '#1976d2' : '#94a3b8' }} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}
